/**
 * The floating widget for background class-content downloads.
 *
 * Deliberately the twin of GlobalUploadManager: same bottom-right anchor, same
 * drag-to-move header, same minimise-on-click, same row layout. Someone who has
 * used the upload widget already knows how this one works, and a download that
 * looked different would read as a different kind of thing than it is.
 *
 * It sits above the upload widget rather than on top of it, so a class being
 * uploaded and a batch being exported can both be watched at once.
 */

import React, { useEffect, useRef, useState } from "react";
import {
    X, ChevronDown, ChevronUp, CheckCircle, AlertCircle, Loader2,
    Download, Package, Ban,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useClassDownload, DownloadJob } from "@/contexts/GlobalDownloadContext";

const PANEL_WIDTH = 320; // w-80
const HEADER_HEIGHT = 56;
const EDGE = 8;
/** Clear of the upload widget's bottom-right anchor. */
const DEFAULT_BOTTOM = 5;

const clampPos = (x: number, y: number) => ({
    x: Math.min(Math.max(EDGE, x), Math.max(EDGE, window.innerWidth - PANEL_WIDTH - EDGE)),
    y: Math.min(Math.max(EDGE, y), Math.max(EDGE, window.innerHeight - HEADER_HEIGHT - EDGE)),
});

const formatBytes = (bytes: number): string => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, i);
    return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
};

const GlobalDownloadManager: React.FC = () => {
    const { downloadQueue, cancelDownload, removeDownload } = useClassDownload();
    const [isMinimized, setIsMinimized] = useState(false);

    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    const dragRef = useRef<{
        startX: number; startY: number; originX: number; originY: number; moved: boolean;
    } | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);

    const running = downloadQueue.some(
        (job) => job.status === "downloading" || job.status === "zipping"
    );

    // A download lives in this tab's memory; closing it mid-run loses the work,
    // so warn the way the browser lets us.
    useEffect(() => {
        if (!running) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [running]);

    useEffect(() => {
        if (!pos) return;
        const onResize = () => setPos((p) => (p ? clampPos(p.x, p.y) : p));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [pos !== null]);

    const handleHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest("button")) return;
        const rect = panelRef.current?.getBoundingClientRect();
        if (!rect) return;
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: rect.left,
            originY: rect.top,
            moved: false,
        };
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    };

    const handleHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        if (!drag.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return; // click, so far
        drag.moved = true;
        setPos(clampPos(drag.originX + dx, drag.originY + dy));
    };

    const handleHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        dragRef.current = null;
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        if (drag && !drag.moved) setIsMinimized((m) => !m);
    };

    if (downloadQueue.length === 0) return null;

    const activeCount = downloadQueue.filter((j) => j.status === "downloading").length;
    const zippingCount = downloadQueue.filter((j) => j.status === "zipping").length;
    const errorCount = downloadQueue.filter((j) => j.status === "error").length;

    return (
        <div
            ref={panelRef}
            className={cn(
                "fixed w-80 bg-white dark:bg-gray-800 shadow-2xl rounded-xl border border-gray-200 dark:border-gray-700 z-[100000] overflow-hidden",
                pos ? "" : "right-5 transition-all duration-300",
                isMinimized ? "h-14" : "max-h-[400px]"
            )}
            style={
                pos
                    ? { left: pos.x, top: pos.y }
                    // Stacked above the upload widget so the two never overlap.
                    : { bottom: `${DEFAULT_BOTTOM}rem` }
            }
        >
            <div
                className="flex items-center justify-between p-4 bg-teal-600 text-white cursor-grab active:cursor-grabbing select-none touch-none"
                onPointerDown={handleHeaderPointerDown}
                onPointerMove={handleHeaderPointerMove}
                onPointerUp={handleHeaderPointerUp}
                onPointerCancel={() => { dragRef.current = null; }}
            >
                <div className="flex items-center gap-2">
                    {activeCount + zippingCount > 0 ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Download className="h-4 w-4" />
                    )}
                    <span className="font-semibold text-sm">
                        {activeCount > 0
                            ? `Downloading ${activeCount} export${activeCount === 1 ? "" : "s"}...`
                            : zippingCount > 0
                                // Every file is in hand and the archive is being
                                // written. Saying "Downloading 100%" here would be
                                // indistinguishable from a finished job.
                                ? `Building ${zippingCount} archive${zippingCount === 1 ? "" : "s"}...`
                                : errorCount > 0
                                    ? `${errorCount} error(s)`
                                    : "All downloads complete"}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
            </div>

            {!isMinimized && (
                <div className="overflow-y-auto max-h-[340px] p-2 space-y-2">
                    {downloadQueue.map((job) => (
                        <DownloadJobCard
                            key={job.id}
                            job={job}
                            onCancel={cancelDownload}
                            onRemove={removeDownload}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const DownloadJobCard: React.FC<{
    job: DownloadJob;
    onCancel: (id: string) => void;
    onRemove: (id: string) => void;
}> = ({ job, onCancel, onRemove }) => {
    const inFlight = job.status === "downloading" || job.status === "zipping";

    // Elapsed time is the only honest signal while a single large recording is
    // in flight: file count does not move for minutes at a time.
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (!inFlight) return;
        const tick = () => setElapsed(Math.floor((Date.now() - job.startedAt) / 1000));
        tick();
        const id = window.setInterval(tick, 1000);
        return () => window.clearInterval(id);
    }, [inFlight, job.startedAt]);

    const elapsedLabel =
        elapsed >= 60 ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s` : `${elapsed}s`;

    return (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate dark:text-gray-200" title={job.label}>
                        {job.label}
                    </p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                        {job.status === "zipping"
                            ? "Writing the archive…"
                            : job.currentFile || `${job.total} file${job.total === 1 ? "" : "s"}`}
                    </p>
                </div>
                <div className="flex items-center gap-1">
                    {inFlight && (
                        <button
                            onClick={() => onCancel(job.id)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-400"
                            title="Cancel this download"
                        >
                            <Ban className="h-3 w-3" />
                        </button>
                    )}
                    <button
                        onClick={() => onRemove(job.id)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-400"
                        title="Remove"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </div>
            </div>

            <div className="space-y-1">
                <div className="flex items-center justify-between text-[10px]">
                    <span
                        className={cn(
                            "font-medium",
                            job.status === "completed" ? "text-green-500" :
                                job.status === "error" ? "text-red-500" :
                                    job.status === "cancelled" ? "text-gray-500" :
                                        job.status === "zipping" ? "text-purple-500" :
                                            "text-teal-500"
                        )}
                    >
                        {job.status === "completed" ? "Saved" :
                            job.status === "error" ? "Failed" :
                                job.status === "cancelled" ? "Cancelled" :
                                    job.status === "zipping" ? `Building archive… ${elapsedLabel}` :
                                        `${job.completed}/${job.total} · ${formatBytes(job.bytes)}`}
                    </span>
                    {job.status === "completed" ? (
                        <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : job.status === "error" ? (
                        <AlertCircle className="h-3 w-3 text-red-500" />
                    ) : job.status === "zipping" ? (
                        <Package className="h-3 w-3 text-purple-500" />
                    ) : null}
                </div>

                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div
                        className={cn(
                            "h-1.5 rounded-full transition-all duration-300",
                            job.status === "completed" ? "bg-green-500" :
                                job.status === "error" ? "bg-red-500" :
                                    job.status === "cancelled" ? "bg-gray-400" :
                                        job.status === "zipping" ? "bg-purple-500 animate-pulse" :
                                            "bg-teal-500"
                        )}
                        style={{ width: `${job.status === "zipping" ? 100 : job.progress}%` }}
                    />
                </div>

                {job.status === "completed" && job.failures > 0 && (
                    <p className="text-[9px] text-amber-600 mt-1 italic">
                        {job.failures} file(s) could not be fetched — see skipped-files.txt in the zip.
                    </p>
                )}

                {/* The archive only exists in this tab, so leaving mid-run loses it. */}
                {inFlight && elapsed > 120 && (
                    <p className="text-[9px] text-teal-600 mt-1 italic">
                        Large export — keep this tab open until it finishes. You can carry
                        on using the app.
                    </p>
                )}

                {job.error && (
                    <p className="text-[9px] text-red-500 mt-1 italic line-clamp-2">
                        {job.error}
                    </p>
                )}
            </div>
        </div>
    );
};

export default GlobalDownloadManager;
